import { useState } from "react";
import { Button, Chip, Tooltip } from "@mui/material";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";

import { formatUsDate } from "helpers";
import { Bucket } from "features/trading/buckets";
import { BucketActions } from "./useBucketActions";
import { BucketInceptionDateModal } from "./BucketInceptionDateModal";

interface BucketInceptionDateProps {
  bucket: Bucket;
  actions: Pick<BucketActions, "setInceptionDate" | "isSettingInceptionDate">;
  // Called once the date is saved or cleared, for the Buckets sub-tab to
  // reload itself. Required, not optional: the whole point is that no
  // caller can mount this control and silently skip the reload.
  onSaved: () => void;
}

/**
 * The bucket's inception date in its header: the date itself as a clickable
 * chip once set, and a button to set a first one while it is still null.
 * Either opens the same picker, so re-picking and clearing live where the
 * value is read.
 *
 * Saving reloads the sub-tab through `onSaved` rather than leaving the new
 * value to the mutation's cache invalidation alone. The invalidation does
 * already refetch the list, so this is belt-and-braces by the operator's
 * explicit choice (Task 3746 interview): the date the user just picked is
 * re-read from the server on the one screen that displays it, whatever
 * happens to the cache tags around it.
 */
export const BucketInceptionDate = ({
  bucket,
  actions,
  onSaved,
}: BucketInceptionDateProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSave = async (inceptionDate: string | null) => {
    const ok = await actions.setInceptionDate(inceptionDate);
    // Only on success: a failed PATCH leaves the modal open on the value the
    // user was editing, and there is nothing new to reload.
    if (!ok) return;
    setIsModalOpen(false);
    onSaved();
  };

  return (
    <>
      {bucket.inception_date ? (
        <Tooltip title="Bucket inception date — click to pick a new one or clear it">
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            icon={<CalendarMonthOutlined />}
            label={`Inception: ${formatUsDate(bucket.inception_date)}`}
            onClick={() => setIsModalOpen(true)}
          />
        </Tooltip>
      ) : (
        <Button
          size="small"
          startIcon={<CalendarMonthOutlined fontSize="small" />}
          onClick={() => setIsModalOpen(true)}
        >
          Set inception date
        </Button>
      )}
      <BucketInceptionDateModal
        open={isModalOpen}
        number={bucket.number}
        inceptionDate={bucket.inception_date}
        saving={actions.isSettingInceptionDate}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
      />
    </>
  );
};
